// Fitment lookup for the product selector.
//
//   Battery Type -> Brand -> Model -> Fuel -> Result
//
// All six battery types live in one file: 534 kB raw, but 21 kB gzipped, which
// any host serves compressed by default. It is fetched once on first use and
// cached for the session — not bundled, so it does not slow the initial page
// load, and not re-fetched when the customer changes battery type.
//
// Generated by build_catalog.py from the Amaron Battery Application Chart.
// Do not hand-edit the JSON; edit the workbook and rebuild.

// Adjust to wherever the JSON is served from.
const URL = "/data/fitments.json";

let cache = null;
let inflight = null;

const loadAll = () => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetch(URL)
    .then((r) => {
      if (!r.ok) throw new Error(`Could not load fitment data (${r.status})`);
      return r.json();
    })
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
};

/** Load (and cache) the fitment tree for one battery type. */
export const loadFitments = async (subtype) => {
  const all = await loadAll();
  const tree = all[subtype];
  if (!tree) throw new Error(`No fitment data for ${subtype}`);
  return tree;
};

/** Optional: call on the Battery Type step to warm the cache early. */
export const preloadFitments = () => loadAll().catch(() => {});

// --- Cascade steps ---------------------------------------------------------

// Brands the customer is most likely to walk in with. Pinned to the top of the
// list; everything else follows alphabetically. Alphabetical-only ordering puts
// Daewoo and Hindustan Motors — both long dead — above Maruti and Hyundai.
export const POPULAR_BRANDS = [
  "Maruti Suzuki", "Hyundai", "Tata", "Mahindra", "Honda", "Toyota", "Kia",
  "Hero", "Honda Motorcycle", "Bajaj", "TVS", "Royal Enfield",
  "Mahindra & Mahindra", "Ashok Leyland", "Eicher", "Sonalika", "Massey Ferguson",
];

const orderBrands = (names) => {
  const pop = POPULAR_BRANDS.filter((b) => names.includes(b));
  const rest = names.filter((b) => !pop.includes(b)).sort((a, b) => a.localeCompare(b));
  return [...pop, ...rest];
};

export const brandsFor = async (subtype) => {
  const data = await loadFitments(subtype);
  const names = Object.keys(data);
  const popular = orderBrands(names).slice(0, POPULAR_BRANDS.filter((b) => names.includes(b)).length);
  return {
    all: orderBrands(names),
    popular,
    // Render as two groups so the common case is one tap, not a long scroll.
    grouped: [
      ...(popular.length ? [{ label: "Common in Sikar", items: popular }] : []),
      { label: "All brands", items: names.slice().sort((a, b) => a.localeCompare(b)) },
    ],
  };
};

export const modelsFor = async (subtype, brand) => {
  const data = await loadFitments(subtype);
  return Object.keys(data[brand] || {}).sort((a, b) => a.localeCompare(b));
};

// Fuel only appears as a step where it actually changes the answer. 46 models
// in the chart take a different battery by fuel (Hyundai Aura 1.2L: petrol and
// CNG take BH40B20L, diesel takes 550114042), so this step cannot be skipped —
// but asking it when there is only one fuel row is a wasted tap.
export const fuelsFor = async (subtype, brand, model) => {
  const data = await loadFitments(subtype);
  const fuels = Object.keys(data[brand]?.[model] || {});
  return fuels.filter((f) => f && f !== "—");
};

export const fuelMatters = async (subtype, brand, model) => {
  const data = await loadFitments(subtype);
  const byFuel = data[brand]?.[model] || {};
  const sets = Object.values(byFuel).map((opts) =>
    opts.map((o) => o.code).sort().join("|")
  );
  return new Set(sets).size > 1;
};

// --- Result ----------------------------------------------------------------

export const warrantyText = (o) =>
  o.fr == null
    ? ""
    : o.pr
      ? `${o.fr} FR + ${o.pr} PR months warranty*`
      : `${o.fr} FR months warranty*`;

const TERMINAL_NOTE =
  "Available in both R and L terminal layouts — we confirm the correct one at fitting.";

/**
 * Batteries that fit, longest warranty first. One part per series, so the
 * customer compares Amaron ranges (premium vs budget) rather than near-identical
 * part numbers.
 */
export const optionsFor = async (subtype, brand, model, fuel) => {
  const data = await loadFitments(subtype);
  const byFuel = data[brand]?.[model] || {};
  const opts = byFuel[fuel] || byFuel["—"] || Object.values(byFuel)[0] || [];
  return opts.map((o) => ({
    ...o,
    warranty: warrantyText(o),
    capacity: o.ah ? `${o.ah} Ah` : "",
    note: o.terminals && o.terminals.length > 1 ? TERMINAL_NOTE : "",
    // Nothing is a dead end: every result can go straight to an enquiry.
    enquiry: {
      subject: `${brand} ${model}${fuel && fuel !== "—" ? ` (${fuel})` : ""} — ${o.seriesLabel} ${o.part}`,
      whatsapp: `https://wa.me/919530498888?text=${encodeURIComponent(
        `Hi, I need an Amaron battery for my ${brand} ${model}. Suggested: ${o.seriesLabel} ${o.part}.`
      )}`,
    },
  }));
};

export const WARRANTY_FOOTNOTE =
  "FR = Free Replacement. PR = Pro-Rata basis. Terms apply.";

// A handful of chart entries reference parts Amaron no longer sells, and 20
// SKUs have no published warranty. Show the battery, omit the warranty line —
// never print "null months".
export const hasPublishedWarranty = (o) => o.fr != null;
